import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";

interface LevelUpModalProps {
  level: number;
  title: string;
  xpEarned: number;
  onDismiss: () => void;
  /** Delay before the holocard appears, so it staggers behind XP toasts. */
  appearDelayMs?: number;
  /** How long the holocard stays before auto-dismissing. */
  autoDismissMs?: number;
}

/**
 * Bottom-right holographic level-up notification.
 * Non-blocking: no backdrop, no overlay, does not capture clicks elsewhere,
 * does not trigger navigation. Auto-dismisses after `autoDismissMs`.
 */
const LevelUpModal = ({
  level,
  title,
  xpEarned,
  onDismiss,
  appearDelayMs = 600,
  autoDismissMs = 3400,
}: LevelUpModalProps) => {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const mountTimer = window.setTimeout(() => {
      setMounted(true);
      requestAnimationFrame(() => setVisible(true));
    }, appearDelayMs);
    return () => window.clearTimeout(mountTimer);
  }, [appearDelayMs]);

  useEffect(() => {
    if (!mounted) return;
    const dismissTimer = window.setTimeout(() => {
      setVisible(false);
      window.setTimeout(onDismiss, 300);
    }, autoDismissMs);
    return () => window.clearTimeout(dismissTimer);
  }, [mounted, autoDismissMs, onDismiss]);

  if (!mounted) return null;

  return (
    <div
      className={`fixed bottom-24 right-4 sm:bottom-6 sm:right-6 z-[200] pointer-events-none transition-all duration-300 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
      }`}
    >
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-auto relative w-[260px] rounded-2xl border border-primary/40 bg-card/90 backdrop-blur-xl px-4 py-3 shadow-[0_0_40px_hsl(var(--primary)/0.25)] overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 pointer-events-none" />
        <button
          onClick={() => {
            setVisible(false);
            window.setTimeout(onDismiss, 200);
          }}
          aria-label="Dismiss"
          className="absolute top-1.5 right-1.5 p-1 rounded-md text-muted-foreground/70 hover:text-foreground hover:bg-foreground/5 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        <div className="relative flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0 flex-1 pr-4">
            <p className="text-[10px] text-primary uppercase tracking-[0.2em] font-semibold">
              Level Up
            </p>
            <p className="font-heading text-sm font-bold text-foreground leading-tight mt-0.5 truncate">
              Level {level} — {title}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">+{xpEarned} XP earned</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LevelUpModal;
